'use client';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchWithAuth } from '@/lib/utils/api-auth';
import type { AIModelDefinition, AIProviderStatus } from '@/types/ai';
import { useAuth } from '@/contexts/AuthContext';
import type { BillingConfig } from '@/types/billing';

export type ModelHealthEntry = {
  status: string; // "ok" | "rate_limited" | "degraded" | "error"
  message?: string;
  updatedAt?: string;
};

export type GlobalAISettings = {
  default_model: string;
  api_keys?: {
    openai: boolean;
    anthropic: boolean;
    google: boolean;
  };
  auth_methods?: {
    openai: 'cli' | 'api_key' | 'auto';
    anthropic: 'cli' | 'api_key' | 'auto';
    google: 'cli' | 'api_key' | 'auto';
  };
  billing_config?: BillingConfig;
};

type GlobalSettingsCtx = {
  settings: GlobalAISettings;
  setSettings: React.Dispatch<React.SetStateAction<GlobalAISettings>>;
  refresh: () => Promise<void>;
  settingsLoading: boolean;
  settingsError: string | null;
  models: AIModelDefinition[];
  providers: AIProviderStatus[];
  modelHealth: Record<string, ModelHealthEntry>;
  modelsLoading: boolean;
  modelsError: string | null;
  refreshModels: () => Promise<void>;
};

const defaultSettings: GlobalAISettings = {
  default_model: '',
  api_keys: {
    openai: false,
    anthropic: false,
    google: false,
  },
  auth_methods: {
    openai: 'auto',
    anthropic: 'auto',
    google: 'auto',
  },
  billing_config: {
    adminBypass: true,
  },
};

const Ctx = createContext<GlobalSettingsCtx | null>(null);

export function useGlobalSettings() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useGlobalSettings must be used within GlobalSettingsProvider');
  return ctx;
}

export default function GlobalSettingsProvider({ children }: { children: React.ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const [settings, setSettings] = useState<GlobalAISettings>(defaultSettings);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [models, setModels] = useState<AIModelDefinition[]>([]);
  const [providers, setProviders] = useState<AIProviderStatus[]>([]);
  const [modelHealth, setModelHealth] = useState<Record<string, ModelHealthEntry>>({});
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState<string | null>(null);
  // Global settings (API keys, billing) — admin-only.
  // Bug 3 (anonymous home page): require an actual `user` object so a race
  // where `authRequired` defaults to `false` (e.g. /api/auth/me unreachable)
  // can no longer leak an unauthenticated /api/settings/global request.
  const canReadGlobalSettings = authLoading ? false : !!user && user.role === 'ADMIN';
  // Model list is not sensitive — any authenticated user can load it. We
  // gate strictly on `user` here too so anonymous visitors don't trigger
  // /api/settings/models (which would 401 and surface "No models discovered"
  // on the marketing page).
  const canLoadModels = authLoading ? false : !!user;

  const refresh = useCallback(async () => {
    if (!canReadGlobalSettings) {
      setSettings(defaultSettings);
      setSettingsError(null);
      setSettingsLoading(false);
      return;
    }

    setSettingsLoading(true);
    setSettingsError(null);
    try {
      const res = await fetchWithAuth('/api/settings/global');
      if (res.ok) {
        const s = await res.json();
        setSettings(s);
      } else {
        setSettings(defaultSettings);
        setSettingsError(`Failed to load global settings (${res.status}).`);
      }
    } catch (e) {
      console.warn('Failed to refresh global settings', e);
      setSettings(defaultSettings);
      setSettingsError('Failed to load global settings.');
    } finally {
      setSettingsLoading(false);
    }
  }, [canReadGlobalSettings]);

  const refreshModels = useCallback(async () => {
    if (!canLoadModels) {
      setModels([]);
      setProviders([]);
      setModelHealth({});
      setModelsError(null);
      setModelsLoading(false);
      return;
    }

    setModelsLoading(true);
    setModelsError(null);
    try {
      const res = await fetchWithAuth('/api/settings/models');
      if (res.ok) {
        const data = (await res.json()) as {
          models?: AIModelDefinition[];
          providers?: AIProviderStatus[];
          modelHealth?: Record<string, ModelHealthEntry>;
          error?: string;
          default_model?: string;
          defaultModel?: string;
        };
        setModels(Array.isArray(data.models) ? data.models : []);
        setProviders(Array.isArray(data.providers) ? data.providers : []);
        setModelHealth(data.modelHealth && typeof data.modelHealth === 'object' ? data.modelHealth : {});
        const safeDefaultModel =
          typeof data.default_model === 'string'
            ? data.default_model
            : typeof data.defaultModel === 'string'
              ? data.defaultModel
              : '';
        if (safeDefaultModel.trim().length > 0) {
          setSettings((prev) => ({ ...prev, default_model: safeDefaultModel.trim() }));
        }
        setModelsError(typeof data.error === 'string' && data.error.trim().length > 0 ? data.error.trim() : null);
      } else {
        setModels([]);
        setProviders([]);
        setModelHealth({});
        setModelsError(`Failed to load model catalog (${res.status}).`);
      }
    } catch (e) {
      console.warn('Failed to refresh models', e);
      setModels([]);
      setProviders([]);
      setModelHealth({});
      setModelsError('Failed to load model catalog.');
    } finally {
      setModelsLoading(false);
    }
  }, [canLoadModels]);

  // Load settings and models on mount.
  //
  // Both `refresh` and `refreshModels` synchronously dispatch
  // setSettingsLoading(true) / setModelsLoading(true) at the top of their
  // bodies before kicking off the network request. Calling them straight
  // from a synchronous effect tick would surface as set-state-in-effect.
  // We dispatch them through a microtask (Promise.resolve().then) so the
  // setState calls land *after* the current render commit — the canonical
  // shape the rule allows for "defer an imperative load".
  useEffect(() => {
    if (authLoading) return;
    void Promise.resolve().then(() => {
      refresh();
      refreshModels();
    });
  }, [authLoading, refresh, refreshModels]);

  // Auto-refresh model health every 60 seconds so passive health data
  // (recorded server-side after execution failures) propagates to the UI
  // without requiring a manual page reload.
  useEffect(() => {
    if (!canLoadModels || authLoading) return;
    const interval = setInterval(() => {
      refreshModels();
    }, 60_000);
    return () => clearInterval(interval);
  }, [canLoadModels, authLoading, refreshModels]);

  const value = useMemo(
    () => ({
      settings,
      setSettings,
      refresh,
      settingsLoading,
      settingsError,
      models,
      providers,
      modelHealth,
      modelsLoading,
      modelsError,
      refreshModels,
    }),
    [
      settings,
      refresh,
      settingsLoading,
      settingsError,
      models,
      providers,
      modelHealth,
      modelsLoading,
      modelsError,
      refreshModels,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
